import { FileCode2, Globe, Monitor, ArrowLeft } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import type { ElementType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAllIntegrations, useApiKey } from "../../../hooks/QueryHooks";
import useSelectWorkspace from "../../../hooks/useSelectWorkspace";
import { cn } from "../../../lib/utils";
import { Badge } from "../../../components/ui/badge";
import { CompanyLogo, getDomainFromCompany } from "../../../components/ui/CompanyLogo";
import notify from "../../../utils/notify";
import stepSelector from "../../../utils/stepSelector";
import NativeSteps from "./components/NativeSteps";

type SDKMethod = "Browser" | "Node" | "Api";

const SDK_OPTIONS: { id: SDKMethod; label: string; icon: ElementType; description: string }[] = [
  { id: "Browser", label: "Browser", icon: Monitor, description: "JavaScript tracking script" },
  { id: "Node", label: "Node.js", icon: FileCode2, description: "Server-side SDK" },
  { id: "Api", label: "API", icon: Globe, description: "REST API integration" },
];

const NativeApiPage = () => {
  const [selectedMethod, setSelectedMethod] = useState<SDKMethod>("Browser");
  const selectedWorkspace: any = useSelectWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const setupRootRef = useRef<HTMLDivElement>(null);
  const workspaceSlug = selectedWorkspace?.data?.unique_identifier;
  const workspaceId = selectedWorkspace?.data?.id;
  const isOnboarding = location.pathname.includes("/onboarding/");

  const apiKey: any = useApiKey({
    workspaceId,
    enabled: !!workspaceId,
  });

  const { data: nativeIntegration }: any = useAllIntegrations({
    workspaceId,
    enabled: !!workspaceId,
    select: (response: any) => response?.data?.find((item: any) => item?.source_name === "native"),
    onError: () => {
      notify({
        heading: "Failed to fetch integrations",
        messageList: ["Please try again later or contact support."],
        type: "failure",
      });
    },
  });

  const selectedOption = useMemo(
    () => SDK_OPTIONS.find((option) => option.id === selectedMethod) || SDK_OPTIONS[0],
    [selectedMethod],
  );

  const steps = useMemo(() => stepSelector(
    selectedMethod,
    selectedWorkspace?.data?.unique_identifier,
    selectedMethod === "Api" ? apiKey?.data?.server_analytics_key : apiKey?.data?.js_analytics_key,
  ), [selectedMethod, selectedWorkspace?.data?.unique_identifier, apiKey?.data?.server_analytics_key, apiKey?.data?.js_analytics_key]);

  const handleBack = () => {
    navigate(-1);
  };

  const statusLabel = nativeIntegration?.setup_complete ? "Connected" : "Disconnected";
  const statusVariant = nativeIntegration?.setup_complete ? "active" : "error";

  return (
    <div className="w-full space-y-6">
      {/* Back + header */}
      <div>
        <button
          type="button"
          onClick={handleBack}
          className="mb-4 flex items-center gap-1 text-[13px] transition-colors hover:text-foreground"
          style={{ color: "#5C6070" }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {isOnboarding ? "Back to integrations" : "Connected Apps"}
        </button>

        <div className="flex items-center gap-3">
          <CompanyLogo
            domain={getDomainFromCompany("Hyperengage")}
            name="Hyperengage"
            className="h-9 w-9"
          />
          <div>
            <h1 className="text-[15px] font-semibold" style={{ color: "#181818" }}>Hyperengage SDK</h1>
            <div className="mt-0.5 flex items-center gap-2">
              <Badge variant="statusPill" status={statusVariant as any}>{statusLabel}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Status card */}
      <div className="rounded-lg border border-border divide-y divide-border">
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[12px]" style={{ color: "#5C6070" }}>Status</span>
          <span className="text-[12px]" style={{ color: "#181818" }}>{nativeIntegration?.setup_complete ? "Active" : "Inactive"}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[12px]" style={{ color: "#5C6070" }}>Credentials</span>
          <span className="text-[12px]" style={{ color: "#181818" }}>
            {apiKey?.data?.js_analytics_key || apiKey?.data?.server_analytics_key ? "Valid" : "Invalid"}
          </span>
        </div>
      </div>

      {/* SDK method + steps */}
      <div ref={setupRootRef} className="space-y-5 rounded-lg border border-border p-5">
        <div>
          <h2 className="text-[15px] font-semibold" style={{ color: "#181818" }}>SDK method</h2>
          <p className="mt-1 text-[13px]" style={{ color: "#5C6070" }}>
            Pick the implementation path you want to use for this workspace.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {SDK_OPTIONS.map((option) => {
              const isActive = selectedMethod === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedMethod(option.id)}
                  className={cn(
                    "flex items-center gap-2 rounded-[5px] border px-3 text-[12px] font-medium transition-all",
                    isActive
                      ? "border-primary bg-accent-tint text-primary"
                      : "border-border bg-card text-muted-foreground hover:border-border-chip hover:bg-muted",
                  )}
                  style={{ height: 28 }}
                >
                  <option.icon className="h-4 w-4" />
                  {option.label}
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-[12px]" style={{ color: "#B0B5C0" }}>
            {selectedOption.label}: {selectedOption.description}
          </p>
        </div>

        <div className="border-t border-border pt-5">
          <NativeSteps
            steps={steps}
            api={selectedMethod === "Api"}
            data={{ apiKey: apiKey?.data, workspace: selectedWorkspace?.data }}
            integration={nativeIntegration}
          />
        </div>
      </div>
    </div>
  );
};

export default NativeApiPage;
